import * as PopoverPrimitive from "@radix-ui/react-popover";
import { useSheetContainer } from "../hooks/useSheetContainer";
import { cn } from "../lib/utils";
import * as React from "react";
import { useEffect, useState } from "react";
const PopoverRoot = PopoverPrimitive.Root;
const PopoverTrigger = PopoverPrimitive.Trigger;
const PopoverPortal = PopoverPrimitive.Portal;
const PopoverAnchor = PopoverPrimitive.Anchor;
/**
 * The floating panel of a popover, styled and animated, rendered through a
 * portal by default. Use it with PopoverRoot / PopoverTrigger when you need
 * full control over the popover's structure.
 * @summary Floating panel of a popover.
 */
const PopoverContent = React.forwardRef(({
  className,
  align = "center",
  sideOffset = 4,
  mountPortal = true,
  mountPortalContainer,
  fullWidth = false,
  preventAutoFocusOnClose = true,
  onCloseAutoFocus,
  ...props
}, ref) => {
  const handleCloseAutoFocus = React.useCallback(event => {
    if (preventAutoFocusOnClose) {
      event.preventDefault();
    }
    onCloseAutoFocus === null || onCloseAutoFocus === void 0 || onCloseAutoFocus(event);
  }, [preventAutoFocusOnClose, onCloseAutoFocus]);
  const content = /*#__PURE__*/React.createElement(PopoverPrimitive.Content, {
    ref: ref,
    align: align,
    sideOffset: sideOffset,
    className: cn("data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95", "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95", "data-[side=bottom]:slide-in-from-top-2", "data-[side=left]:slide-in-from-right-2", "data-[side=right]:slide-in-from-left-2", "data-[side=top]:slide-in-from-bottom-2", "z-50 rounded-xl border shadow-md outline-hidden", "border border-border", "bg-overlay-background", "text-primary-950", fullWidth ? "grow" : "w-72 p-4", className),
    onCloseAutoFocus: handleCloseAutoFocus,
    ...props
  });
  const container = useSheetContainer(mountPortalContainer);
  return mountPortal ? /*#__PURE__*/React.createElement(PopoverPrimitive.Portal, {
    container: container
  }, content) : content;
});
/**
 * Floating content revealed from a trigger, built on Radix Popover. This
 * all-in-one API takes `trigger` and `content`; drop to PopoverRoot /
 * PopoverTrigger / PopoverContent for full control over the structure. To
 * anchor to an element other than the trigger use AnchoredPopover; for a menu
 * of actions use Dropdown.
 * @summary Trigger-anchored floating content.
 */
function Popover({
  trigger,
  popoverTriggerAsChild = false,
  content,
  ...props
}) {
  return /*#__PURE__*/React.createElement(PopoverRoot, null, /*#__PURE__*/React.createElement(PopoverTrigger, {
    asChild: popoverTriggerAsChild
  }, trigger), /*#__PURE__*/React.createElement(PopoverContent, {
    ...props
  }, content));
}
PopoverContent.displayName = PopoverPrimitive.Content.displayName;
/**
 * A popover positioned against an arbitrary anchor element instead of a
 * trigger, tracking the anchor through scrolling and resizing. Use it when
 * the popover must open next to an element that is not its trigger; for the
 * common trigger-anchored case, use Popover.
 * @summary Popover anchored to an arbitrary element.
 */
function AnchoredPopover({
  open,
  anchorRef,
  children,
  className,
  ...props
}) {
  const [position, setPosition] = useState({
    top: "50%",
    left: "50%",
    width: "0px",
    height: "0px"
  });
  useEffect(() => {
    if (!open) {
      return;
    }
    const updatePosition = () => {
      if (!(anchorRef !== null && anchorRef !== void 0 && anchorRef.current)) {
        setPosition({
          top: "50%",
          left: "50%",
          width: "0px",
          height: "0px"
        });
        return;
      }
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition({
        top: `${rect.top}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`
      });
    };
    updatePosition();
    const resizeObserver = new ResizeObserver(updatePosition);
    if (anchorRef !== null && anchorRef !== void 0 && anchorRef.current) {
      resizeObserver.observe(anchorRef.current);
    }
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, anchorRef]);
  return /*#__PURE__*/React.createElement(PopoverRoot, {
    open: open,
    modal: false
  }, /*#__PURE__*/React.createElement(PopoverAnchor, {
    className: "fixed transition-all duration-300 ease-in-out",
    style: {
      top: position.top,
      left: position.left,
      width: position.width,
      height: position.height
    }
  }), /*#__PURE__*/React.createElement(PopoverContent, {
    ...props,
    onOpenAutoFocus: e => e.preventDefault(),
    mountPortal: false,
    className: cn(className, !anchorRef && "translate-y-[-50%]")
  }, children));
}
export { AnchoredPopover, Popover, PopoverAnchor, PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger };
//# sourceMappingURL=Popover.js.map